import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./SelectAudience.css";
import "../../components/header/userMenu/SettingsPrivacy.css";

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const GroupIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const FriendsExceptIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    <line x1="18" y1="6" x2="24" y2="0" strokeWidth="2.5" />
  </svg>
);

const PersonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <circle cx="12" cy="16" r="1" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const audienceOptions = [
  {
    value: "public",
    label: "Public",
    description: "Anyone on or off Facebook",
    Icon: GlobeIcon,
  },
  {
    value: "friends",
    label: "Friends",
    description: "Your friends on Facebook",
    Icon: GroupIcon,
  },
  {
    value: "friends_except",
    label: "Friends except...",
    description: "Don't show to some friends",
    Icon: FriendsExceptIcon,
  },
  {
    value: "specific_friends",
    label: "Specific friends",
    description: "Only show to some friends",
    Icon: PersonIcon,
  },
  {
    value: "only_me",
    label: "Only me",
    description: "Only you can see",
    Icon: LockIcon,
  },
];

const visibilityLabels = {
  public: "Public",
  friends: "Friends",
  friends_except: "Friends except...",
  specific_friends: "Specific friends",
  only_me: "Only me",
  custom: "Custom",
};

export default function SelectAudience() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state);
  const [selectedAudience, setSelectedAudience] = useState(
    () => localStorage.getItem("postVisibility") || "friends"
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      localStorage.setItem("postVisibility", selectedAudience);
      if (user && user.token) {
        try {
          // API call to persist post visibility would be placed here.
          // await axios.post(`${API_URL}/api/user/postVisibility`, { visibility: selectedAudience }, {
          //   headers: { Authorization: `Bearer ${user.token}` },
          // });
        } catch (err) {
          console.error("Failed to update post visibility:", err);
        }
      }
      navigate("/settings", { replace: true });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="audience_page">
      <header className="audience_page_header">
        <button className="audience_page_back" onClick={handleBack} aria-label="Go back">
          <BackIcon />
        </button>
        <h1>Select audience</h1>
      </header>

      <main className="audience_page_body">
        <section className="audience_page_info audience_modal_info">
          <h2 className="audience_page_title audience_modal_heading">Who can see your future posts?</h2>
          <p className="audience_page_description audience_modal_description">
            Your default audience is set to <strong>{visibilityLabels[selectedAudience]}</strong>. This will be your audience for future
            posts, but you can always change it for a specific post.
          </p>
        </section>

        <section className="audience_page_options audience_options_list">
          {audienceOptions.map(({ value, label, description, Icon }) => (
            <label
              key={value}
              className={`audience_option ${selectedAudience === value ? "selected" : ""}`}
              onClick={() => setSelectedAudience(value)}
            >
              <div className="audience_option_icon">
                <Icon />
              </div>
              <div className="audience_option_content">
                <span className="audience_option_label">{label}</span>
                <span className="audience_option_desc">{description}</span>
              </div>
              <input
                type="radio"
                name="audience"
                value={value}
                checked={selectedAudience === value}
                onChange={() => setSelectedAudience(value)}
              />
            </label>
          ))}
        </section>
      </main>

      <footer className="audience_page_footer audience_modal_footer">
        <button type="button" className="audience_modal_cancel" onClick={handleBack} disabled={isSaving}>
          Cancel
        </button>
        <button type="button" className="audience_modal_done" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Done"}
        </button>
      </footer>
    </div>
  );
}

